import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import * as ApolloWrappers from 'graphql-client/ApolloWrappers';
import {ProteinStructureExternalFile, ProteinVersion} from 'molbio/proteins/util/ProteinConstants';
import {StructurePredictionModel} from 'molbio/proteins/util/StructurePredictionConstants';

import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';
import * as AlphaFoldDAO from './AlphaFoldDAO';
import {
  HAS_IN_PROGRESS_ALPHAFOLD_PREDICTION,
  REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY,
} from './AlphaFoldTabContent';

export async function requestRun(
  proteinId: string,
  remainingExecutionsAndTenantNameData: RemainingExecutionsAndTenantNameQuery,
  selectedModel: StructurePredictionModel
): Promise<void> {
  if (BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS) {
    await AlphaFoldDAO.runStructurePrediction([proteinId], selectedModel);
  } else {
    await AlphaFoldDAO.runAlphaFold(proteinId);
  }

  // Update Apollo cache to reflect the new prediction in progress
  ApolloWrappers.writeQuery(
    {query: REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY},
    {
      ...remainingExecutionsAndTenantNameData,
      remainingAlphafoldExecutions: remainingExecutionsAndTenantNameData.remainingAlphafoldExecutions - 1,
    }
  );
  ApolloWrappers.writeQuery(
    {query: HAS_IN_PROGRESS_ALPHAFOLD_PREDICTION},
    {
      protein: {__typename: 'ProteinNode', id: proteinId, alphafoldPredictionInProgress: true},
    },
    {proteinId}
  );
}

interface UnavailableStructureInfo {
  structureFile: null;
  model: null;
  modelVersion: null;
  versionId: null;
}

interface AvailableStructureInfo {
  structureFile: ProteinStructureExternalFile;
  model: string;
  modelVersion: string;
  structureFileOutOfDate: boolean;
  versionId: number;
}

/**
 * Utility to evaluate info about a Protein's AlphaFold structure files.
 *
 * structureFile: the most recent completed structure file available, or undefined if there are none.
 * structureFileOutOfDate: indicates whether structureFile belongs to the most recent protein version.
 */
export function getStructureInfoFromVersions(
  versions?: Array<ProteinVersion>
): UnavailableStructureInfo | AvailableStructureInfo {
  if (versions === undefined || versions.length === 0) {
    return {structureFile: null, model: null, modelVersion: null, versionId: null};
  }
  const currentVersionStructures = versions[versions.length - 1].structures;
  if (currentVersionStructures.length) {
    const versionId = versions[versions.length - 1].id;
    const structure = currentVersionStructures[0];
    return {
      structureFile: structure.externalFile,
      model: structure.model,
      modelVersion: structure.modelVersion,
      structureFileOutOfDate: false,
      versionId,
    };
  }

  const flattenedVersionStructures = versions.flatMap((version) =>
    version.structures.map((structure) => ({
      structure,
      versionId: version.id,
      model: structure.model,
      modelVersion: structure.modelVersion,
    }))
  );
  if (flattenedVersionStructures.length) {
    const {versionId, structure, model, modelVersion} =
      flattenedVersionStructures[flattenedVersionStructures.length - 1];
    return {
      structureFile: structure.externalFile,
      model,
      modelVersion,
      structureFileOutOfDate: true,
      versionId,
    };
  }
  return {structureFile: null, model: null, modelVersion: null, versionId: null};
}

export async function downloadStructureFile(
  structureFile: ProteinStructureExternalFile,
  proteinId: string,
  versionId: number
): Promise<void> {
  const signedURL = await performSignedFileQuery(structureFile.id, structureFile.url, proteinId, versionId);
  if (signedURL == null) {
    return;
  }
  window.open(signedURL, '_blank', 'noopener noreferrer');
  trackEvent(MOLBIO_ANALYTICS_ACTIONS.DOWNLOADED_ALPHAFOLD_STRUCTURE);
}
